import {Injectable} from '@angular/core';
import {AngularFireStorage, AngularFireStorageReference, AngularFireUploadTask} from '@angular/fire/compat/storage';
// Importação correta e necessária do forkJoin
import {Observable, forkJoin} from 'rxjs';
import {finalize, switchMap, map} from 'rxjs/operators';

// Interface para estruturar os metadados do arquivo
export interface StoredFile {
  name: string;
  fullPath: string;
  downloadURL: string;
  size?: number; // Tamanho em bytes
  timeCreated?: Date;
}

@Injectable({
  providedIn: 'root'
})
export class FileManagerService {

  private BASE_PATH = ''; // Pasta de destino no Firebase Storage

  set storage_path (path: string) {
    this.BASE_PATH = path;
  }

  constructor (private storage: AngularFireStorage) { }

  /**
   * Faz o upload de um arquivo para a pasta 'site_pub'.
   * @param file O arquivo a ser carregado (objeto File do JS).
   * @returns Um objeto contendo a tarefa de upload e o Observable do URL de download.
   */
  uploadFile (file: File): {task: AngularFireUploadTask, downloadURL$: Observable<string | null>} {
    const filePath = `${ this.BASE_PATH }/${ file.name }`;
    const fileRef: AngularFireStorageReference = this.storage.ref(filePath);
    const task: AngularFireUploadTask = this.storage.upload(filePath, file);

    // Monitora o estado da tarefa e, ao finalizar, obtém o URL de download
    const downloadURL$ = task.snapshotChanges().pipe(
      finalize(() => { }), // Finalize é chamado quando o upload termina (sucesso ou erro)
      switchMap(snapshot => {
        if (snapshot?.state === 'success') {
          return fileRef.getDownloadURL();
        }
        // Se o snapshot não for 'success' (ou for null), retorna um Observable que emite null
        return new Observable<string | null>(subscriber => {
          if (snapshot?.state !== 'running' && snapshot?.state !== 'paused') {
            subscriber.next(null);
            subscriber.complete();
          }
        });
      })
    );

    return {task, downloadURL$};
  }

  /**
   * Lista todos os arquivos (e seus metadados) dentro da pasta 'site_pub'.
   * @returns Observable de uma lista de objetos StoredFile.
   */
  listFiles (): Observable<StoredFile[]> {
    const listRef = this.storage.ref(this.BASE_PATH);

    //console.log('Tentando listar arquivos no caminho:', this.BASE_PATH);

    return listRef.listAll().pipe(
      switchMap(result => {
        const fileObservables: Observable<StoredFile>[] = result.items.map(itemRef => {
          // Combina getDownloadURL e getMetadata para cada item
          const url$ = itemRef.getDownloadURL();
          const metadata$ = itemRef.getMetadata();

          return forkJoin({url: url$, metadata: metadata$}).pipe(
            map(({url, metadata}) => ({
              name: itemRef.name,
              fullPath: itemRef.fullPath,
              downloadURL: url,
              size: metadata.size,
              timeCreated: metadata.timeCreated ? new Date(metadata.timeCreated) : undefined
            }))
          );
        });

        // Se houver arquivos, usa forkJoin para aguardar todos os Observables de arquivos.
        // Se a pasta estiver vazia, retorna um Observable que emite um array vazio.
        return fileObservables.length > 0 ? forkJoin(fileObservables) : new Observable<StoredFile[]>(subscriber => {
          subscriber.next([]);
          subscriber.complete();
        });
      })
    );
  }

  /**
   * Apaga um arquivo do Firebase Storage usando seu caminho completo.
   * @param fullPath O caminho completo do arquivo no Storage (ex: 'site_pub/imagem.jpg').
   * @returns Uma Promise que resolve quando o arquivo é apagado.
   */
  // O uso de .toPromise() é comum no AngularFire/RxJS para converter Observables
  // para Promises quando não se deseja gerenciar subscriptions.
  deleteFile (fullPath: string): Promise<void> {
    const fileRef = this.storage.ref(fullPath);
    return fileRef.delete().toPromise();
  }
}